'use strict';

import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react';
import {useSelector} from 'react-redux';

import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import {PropertyValueType} from '~/shared/src/mm/PropertySet';
import {
    ColumnDefinition,
    ColumnFilter,
    FilterMatchMode
} from '~/shared/src/validationSchemes/modules/overview';
import {
    buildDataDescriptor,
    getReferenceSetsForDataObject,
    DataDescriptor,
    DataStructure
} from '~/shared/src/utilities/dataStructure';
import dataUtils from '~/shared/src/utilities/dataUtils';
import {FilterConfig} from '~/shared/src/utilities/filterConfig';

import {Button} from 'primereact/button';
import {
    Dropdown,
    DropdownChangeEvent
} from 'primereact/dropdown';

import FilterField from '~/frontend/src/components/base/shared/field/FilterField';

import {StoreState} from '~/frontend/src/store';
import {
    getItemKeyDisplayName,
    getItemNameDisplayName,
    getPropertyKeyDisplayName,
    getPropertyValueDisplayName
} from '~/frontend/src/utilities/renderHelpers';
import {defaultBaseRequestParameters} from '~/shared/src/utilities/modules/common';
import {
    FormFieldProperties,
    valueTypeToFieldTypeMapping
} from '~/shared/src/utilities/modules/form';
import {getDefaultFilterMatchMode} from '~/shared/src/utilities/filterUtils';


export type Field = {
    field : string;
    label : string;
    valueType : PropertyValueType;
    valueSetId? : number;
    valueUnit? : string;
};
export type FieldGroup = {
    id : number;
    label : string | JSX.Element;
    fields : Field[]
};
type Fields = ( Field | FieldGroup )[];

type DefaultProps = {
    disabled : boolean;
    limitMappingsByValueType : boolean;

    /**
     * Should the field marked if dirty.
     */
    markDirty? : boolean;
};
const defaultProps : Required<DefaultProps> = {
    disabled                : false,
    limitMappingsByValueType: true,
    markDirty               : false
};

type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    columnDefinitions : ColumnDefinition[];

    dataStructureForField : DataStructure;

    dataStructureForValue? : DataStructure;

    defaultFilters : ColumnFilter[];

    /**
     * The value.
     */
    value? : ColumnFilter[];

    /**
     * The value.
     */
    originalValue? : ColumnFilter[];

    tabIndex? : number;

    onChange : ( value : ColumnFilter[] | undefined ) => void;
};

type AllProps = Required<DefaultProps> & Props;


const FiltersField = ( props : AllProps ) => {
    const {
              columnDefinitions,
              dataStructureForField,
              dataStructureForValue,
              defaultFilters,
              disabled,
              limitMappingsByValueType,
              markDirty,
              onChange,
              tabIndex,
              uid,
              value
          }                                 = props,
          wrapperClassName : string         = 'field mb-0 yn-subfield-wrapper',
          user                              = useSelector( ( state : StoreState ) => state.session?.user ? state.session.user : undefined ),
          messageCatalogTimestamp           = useSelector( ( state : StoreState ) => state.messagecatalog?.timestamp ? state.messagecatalog.timestamp : 0 ),
          [ elementValue, setElementValue ] = useState<ColumnFilter[] | undefined>( value ? value : defaultFilters ),
          language                          = useMemo(
              () => {
                  return user?.language ?? '';
              },
              [
                  user
              ]
          ),
          schema                            = useMemo(
              () => {
                  return ( dataStructureForField?.item?.objectSchema?.toUpperCase() ?? 'DATA' ) as Schema;
              },
              [
                  dataStructureForField
              ]
          ),
          originalValue                     = useMemo(
              () => {
                  return props.originalValue ?? defaultFilters ?? [];
              },
              [
                  defaultFilters,
                  props.originalValue
              ]
          ),
          getFieldsForDataObject            = ( dataObject : DataStructure, asLabel : boolean = true ) : Field[] => {
              const innerRetVal : Field[] = [],
                    objectSchema          = ( dataObject?.item?.objectSchema?.toUpperCase() ?? 'DATA' ) as Schema;

              if ( dataObject.item.objectType ) {
                  const propertySets = metaModel[ objectSchema ].getPropertySetsForItemType( dataObject.item.objectType );

                  let attributePath : string,
                      dataDescriptor : DataDescriptor;

                  propertySets.sort(
                      ( pset1, pset2 ) => pset1.label.toLowerCase() > pset2.label.toLowerCase() ? 1 : ( pset1.label.toLowerCase() < pset2.label.toLowerCase() ? -1 : 0 )
                  );

                  attributePath = 'item.id';
                  dataDescriptor = buildDataDescriptor( { attributePath }, dataObject )!;

                  innerRetVal.push( {
                      field    : asLabel
                          ? dataDescriptor.alias
                          : `${dataObject.item.key}.${dataDescriptor.attributePath}`,
                      label    : __( 'YN.base.common.attribute.id' ),
                      valueType: 'INTEGER'
                  } );

                  attributePath = 'item.key';
                  dataDescriptor = buildDataDescriptor( { attributePath }, dataObject )!;

                  innerRetVal.push( {
                      field    : asLabel
                          ? dataDescriptor.alias
                          : `${dataObject.item.key}.${dataDescriptor.attributePath}`,
                      label    : getItemKeyDisplayName( dataObject.item.objectType, objectSchema ),
                      valueType: ( dataUtils.getDataValueType( dataDescriptor, objectSchema ) ?? 'STRING' ) as PropertyValueType
                  } );

                  attributePath = 'item.name';
                  dataDescriptor = buildDataDescriptor( { attributePath }, dataObject )!;

                  innerRetVal.push( {
                      field    : asLabel
                          ? dataDescriptor.alias
                          : `${dataObject.item.key}.${dataDescriptor.attributePath}`,
                      label    : getItemNameDisplayName( dataObject.item.objectType, objectSchema ),
                      valueType: 'STRING'
                  } );

                  // get possible fields for all properties
                  for ( const propertySet of propertySets ) {
                      if ( propertySet.isEnumerated ) {
                          if ( propertySet.isKeySettable ) {
                              attributePath = `property.${propertySet.name}.key`;
                              dataDescriptor = buildDataDescriptor( { attributePath }, dataObject )!;

                              innerRetVal.push( {
                                  field    : asLabel
                                      ? dataDescriptor.alias
                                      : `${dataObject.item.key}.${dataDescriptor.attributePath}`,
                                  label    : `${propertySet.label} - ${getPropertyKeyDisplayName( propertySet.id, objectSchema )}`,
                                  valueType: propertySet.valueType
                              } );
                          }

                          if ( propertySet.isValueSettable ) {
                              attributePath = `property.${propertySet.name}.value`;
                              dataDescriptor = buildDataDescriptor( { attributePath }, dataObject )!;

                              innerRetVal.push( {
                                  field     : asLabel
                                      ? dataDescriptor.alias
                                      : `${dataObject.item.key}.${dataDescriptor.attributePath}`,
                                  label     : `${propertySet.label} - ${getPropertyValueDisplayName( propertySet.id, objectSchema )}`,
                                  valueType : propertySet.valueType,
                                  valueSetId: propertySet.valueSetId,
                                  valueUnit : propertySet.valueUnit
                              } );
                          }
                      }
                      else {
                          attributePath = `property.${propertySet.name}`;
                          dataDescriptor = buildDataDescriptor( { attributePath }, dataObject )!;

                          innerRetVal.push( {
                              field     : asLabel
                                  ? dataDescriptor.alias
                                  : `${dataObject.item.key}.${dataDescriptor.attributePath}`,
                              label     : propertySet.label,
                              valueType : propertySet.valueType,
                              valueSetId: propertySet.valueSetId,
                              valueUnit : propertySet.valueUnit
                          } );
                      }
                  }
              }

              return innerRetVal;
          },
          getFieldsForDataStructure         = ( dataStructure : DataStructure, asLabel : boolean = true ) : Fields => {
              const objectSchema = ( dataStructure?.item?.objectSchema?.toUpperCase() ?? 'DATA' ) as Schema;

              let innerRetVal : Fields = [];

              if ( dataStructure.item.objectType ) {
                  const itemType      = metaModel[ objectSchema ].getItemType( dataStructure.item.objectType )!,
                        referenceSets = getReferenceSetsForDataObject( dataStructure, dataStructureForField, objectSchema );

                  innerRetVal.push( {
                      id    : dataStructure.origId,
                      label : `${itemType.label}${referenceSets?.length === 1 ? ` (${referenceSets[ 0 ].referenceTypeLabel})` : ''}`,
                      fields: getFieldsForDataObject( dataStructure, asLabel )
                  } );

                  if ( dataStructure.dataStructure ) {
                      for ( const subDataStructure of dataStructure.dataStructure ) {
                          innerRetVal = innerRetVal.concat( getFieldsForDataStructure( subDataStructure, asLabel ) );
                      }
                  }
              }

              return innerRetVal;
          },
          fields                            = useMemo(
              () => {
                  let retVal : Fields = [];

                  if ( dataStructureForField.origId === 0 ) {
                      for ( const columnDefinition of columnDefinitions ) {
                          retVal.push( {
                              field    : columnDefinition.field,
                              label    : __( columnDefinition.header ),
                              valueType: columnDefinition.valueType ?? 'STRING'
                          } );
                      }
                  }
                  else {
                      if ( !dataStructureForField.dataStructure?.length ) {
                          retVal = getFieldsForDataObject( dataStructureForField );
                      }
                      else {
                          retVal = getFieldsForDataStructure( dataStructureForField );
                      }
                  }

                  return retVal;
              },
              [
                  columnDefinitions,
                  dataStructureForField,
                  language,
                  messageCatalogTimestamp
              ]
          ),
          mappings                          = useMemo(
              () => {
                  let retVal : Fields = [];

                  if ( dataStructureForValue ) {
                      if ( !dataStructureForValue.dataStructure?.length ) {
                          const objectSchema = ( dataStructureForValue?.item?.objectSchema?.toUpperCase() ?? 'DATA' ) as Schema,
                                itemType     = metaModel[ objectSchema ].getItemType( dataStructureForValue.item.objectType ?? '' );

                          retVal = [
                              {
                                  id    : dataStructureForValue.origId,
                                  label : itemType ? itemType.label : __( 'YN.base.jsonField.filtersField.rootItem.mappings.label' ),
                                  fields: getFieldsForDataObject( dataStructureForValue, false )
                              }
                          ];
                      }
                      else {
                          retVal = getFieldsForDataStructure( dataStructureForValue, false );
                      }
                  }

                  // add user mappings
                  retVal.push( {
                      id    : 0,
                      label : __( 'YN.base.jsonField.filtersField.user.mappings.label' ),
                      fields: [
                          {
                              field    : 'user.userId',
                              label    : getItemKeyDisplayName( 'User', 'CFG' ),
                              valueType: 'STRING'
                          },
                          {
                              field    : 'user.name',
                              label    : getItemNameDisplayName( 'User', 'CFG' ),
                              valueType: 'STRING'
                          },
                          {
                              field    : 'user.firstName',
                              label    : metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'User', 'Firstname' )?.label ?? 'Firstname',
                              valueType: 'STRING'
                          },
                          {
                              field    : 'user.lastName',
                              label    : metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'User', 'Lastname' )?.label ?? 'Lastname',
                              valueType: 'STRING'
                          },
                          {
                              field    : 'user.email',
                              label    : metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'User', 'Email' )?.label ?? 'Email',
                              valueType: 'EMAIL'
                          },
                          {
                              field     : 'user.language',
                              label     : metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'User', 'Language' )?.label ?? 'Language',
                              valueType : 'STRING',
                              valueSetId: metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'User', 'Language' )?.valueSetId
                          },
                          {
                              field    : 'user.applicationName',
                              label    : metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'User', 'Application' )?.label ?? 'Application',
                              valueType: 'STRING'
                          }
                      ]
                  } );

                  return retVal;
              },
              [
                  dataStructureForValue,
                  language,
                  messageCatalogTimestamp
              ]
          ),
          changeElementValue                = useCallback(
              ( newElementValue : ColumnFilter[] ) => {
                  setElementValue( newElementValue );

                  if ( !newElementValue.find( nEV => nEV.value === '' || nEV.value === undefined || nEV.value === null ) ) {
                      if ( JSON.stringify( newElementValue ) === JSON.stringify( defaultFilters ) || !newElementValue.length ) {
                          onChange( undefined );
                      }
                      else {
                          onChange( newElementValue );
                      }
                  }
              },
              [
                  defaultFilters,
                  onChange
              ]
          ),
          onDeleteButtonClick               = useCallback(
              ( field : Field, entry : ColumnFilter ) => {
                  if ( field ) {
                      const newElementValue = ( [] as ColumnFilter[] ).concat( elementValue ?? [] ),
                            index           = newElementValue.findIndex( ( eV ) => eV.field === field.field );

                      if ( index !== -1 ) {
                          newElementValue.splice( index, 1 );
                      }

                      changeElementValue( newElementValue );
                  }
              },
              [
                  changeElementValue,
                  elementValue
              ]
          ),
          onFieldChange                     = useCallback(
              ( field : Field | undefined, entry : ColumnFilter | undefined, event : DropdownChangeEvent ) => {
                  if ( !field ) {
                      const hasFieldGroups = ( fields?.[ 0 ] as unknown as FieldGroup )?.fields !== undefined;

                      if ( hasFieldGroups ) {
                          const fieldGroups : FieldGroup[] = [].concat( fields as [] );

                          outerFor: for ( const fieldGroup of fieldGroups ) {
                              for ( const fieldItem of fieldGroup.fields ) {
                                  if ( fieldItem.field === event.value?.field ) {
                                      field = fieldItem;
                                      break outerFor;
                                  }
                              }
                          }
                      }
                      else {
                          const tempFields : Field[] = [].concat( fields as [] );

                          for ( const fieldItem of tempFields ) {
                              if ( fieldItem.field === event.value?.field ) {
                                  field = fieldItem;
                                  break;
                              }
                          }
                      }
                  }

                  if ( field && event.value?.field ) {
                      const newElementValue = ( [] as ColumnFilter[] ).concat( elementValue ?? [] ),
                            index           = newElementValue.findIndex( ( eV ) => eV.field === entry?.field );

                      if ( index !== -1 ) {
                          newElementValue[ index ] = {
                              ...newElementValue[ index ],
                              field    : event.value.field,
                              operator : ( field.valueSetId
                                  ? 'equals'
                                  : getDefaultFilterMatchMode( field.valueType ) ) as Exclude<FilterMatchMode, 'empty' | 'notEmpty'>,
                              value    : '',
                              valueType: field.valueType
                          };
                      }
                      else {
                          newElementValue.push( {
                              key      : event.value.field,
                              field    : event.value.field,
                              operator : ( field.valueSetId
                                  ? 'equals'
                                  : getDefaultFilterMatchMode( field.valueType ) ) as Exclude<FilterMatchMode, 'empty' | 'notEmpty'>,
                              value    : '',
                              valueType: field.valueType
                          } );
                      }

                      changeElementValue( newElementValue );
                  }
              },
              [
                  changeElementValue,
                  elementValue,
                  fields
              ]
          ),
          onOperatorChange                  = useCallback(
              ( field : Field | undefined, entry : ColumnFilter | undefined, fieldKey : string, fieldName : string, changedOperator : FilterMatchMode, changedValue : any ) => {
                  if ( field ) {
                      const newElementValue = ( [] as ColumnFilter[] ).concat( elementValue ?? [] ),
                            index           = newElementValue.findIndex( ( eV ) => eV.field === entry?.field );

                      if ( index !== -1 ) {
                          if ( changedOperator !== 'empty' && changedOperator !== 'notEmpty' ) {
                              newElementValue[ index ] = {
                                  ...newElementValue[ index ],
                                  operator : changedOperator,
                                  value    : changedValue,
                                  valueType: field.valueType
                              };
                          }
                          else {
                              newElementValue[ index ] = {
                                  ...newElementValue[ index ],
                                  operator : changedOperator,
                                  value    : undefined,
                                  valueType: undefined
                              };
                          }
                      }

                      changeElementValue( newElementValue );
                  }
              },
              [
                  changeElementValue,
                  elementValue
              ]
          ),
          onValueChange                     = useCallback(
              ( field : Field | undefined, entry : ColumnFilter | undefined, fieldKey : string, fieldName : string, changedOperator : FilterMatchMode, changedValue : any ) => {
                  if ( field ) {
                      const newElementValue = ( [] as ColumnFilter[] ).concat( elementValue ?? [] ),
                            index           = newElementValue.findIndex( ( eV ) => eV.field === entry?.field );

                      if ( index !== -1 ) {
                          if ( changedOperator !== 'empty' && changedOperator !== 'notEmpty' ) {
                              newElementValue[ index ] = {
                                  ...newElementValue[ index ],
                                  operator : changedOperator,
                                  value    : changedValue,
                                  valueType: field.valueType
                              };
                          }
                          else {
                              newElementValue[ index ] = {
                                  ...newElementValue[ index ],
                                  operator : changedOperator,
                                  value    : undefined,
                                  valueType: undefined
                              };
                          }
                      }

                      changeElementValue( newElementValue );
                  }
              },
              [
                  changeElementValue,
                  elementValue
              ]
          ),
          getField                          = ( field : Field | undefined, entry : ColumnFilter | undefined, fieldTabIndex : number ) => {
              const hasFieldGroups = ( fields?.[ 0 ] as unknown as FieldGroup )?.fields !== undefined;

              let availableFields : Fields = [],
                  fieldMarkDirty           = false,
                  fieldMarkDefault         = false;

              if ( hasFieldGroups ) {
                  const tempAvailableFields : FieldGroup[] = [].concat( fields as [] );

                  for ( const fieldToRemove of ( elementValue ?? [] ) ) {

                      outerFor: for ( let k = tempAvailableFields.length - 1; k >= 0; k-- ) {
                          tempAvailableFields[ k ] = Object.assign( {}, tempAvailableFields[ k ] );
                          tempAvailableFields[ k ].fields = [].concat( tempAvailableFields[ k ].fields as [] );

                          for ( let l = tempAvailableFields[ k ].fields.length - 1; l >= 0; l-- ) {
                              if ( tempAvailableFields[ k ].fields[ l ].field !== field?.field && tempAvailableFields[ k ].fields[ l ].field === fieldToRemove.field ) {
                                  tempAvailableFields[ k ].fields.splice( l, 1 );

                                  if ( !tempAvailableFields[ k ].fields.length ) {
                                      tempAvailableFields.splice( k, 1 );
                                  }

                                  break outerFor;
                              }
                          }
                      }
                  }

                  availableFields = tempAvailableFields;
              }
              else {
                  const tempAvailableFields : Field[] = [].concat( fields as [] );

                  for ( const fieldToRemove of ( elementValue ?? [] ) ) {
                      for ( let k = tempAvailableFields.length - 1; k >= 0; k-- ) {
                          if ( tempAvailableFields[ k ].field !== field?.field && tempAvailableFields[ k ].field === fieldToRemove.field ) {
                              tempAvailableFields.splice( k, 1 );
                              break;
                          }
                      }
                  }

                  availableFields = tempAvailableFields;
              }

              if ( entry ) {
                  if ( markDirty ) {
                      const originalValueEntry = originalValue.find( oV => oV.field === entry.field );

                      if (
                          !originalValueEntry
                          || originalValueEntry.field !== entry.field
                          || ( originalValue !== defaultFilters && elementValue === defaultFilters )
                      ) {
                          fieldMarkDirty = true;
                      }
                  }

                  if ( elementValue === defaultFilters ) {
                      fieldMarkDefault = true;
                  }
              }
              else if ( markDirty && !elementValue?.length && JSON.stringify( originalValue ?? '' ) !== JSON.stringify( elementValue ?? '' ) ) {
                  fieldMarkDirty = true;
              }

              return <div className={`${wrapperClassName} yn-jsonfield-filtersfield-field`}>
                  <Dropdown
                      value={field}
                      options={availableFields}
                      onChange={onFieldChange.bind( null, field, entry )}
                      optionLabel="label"
                      optionGroupLabel={hasFieldGroups ? 'label' : undefined}
                      optionGroupChildren={hasFieldGroups ? 'fields' : undefined}
                      style={{ width: '100%' }}
                      className={`${fieldMarkDirty ? 'yn-dirty-border' : undefined}${fieldMarkDefault ? ' status-default' : ''}`}
                      disabled={!field ? false : disabled}
                      tabIndex={fieldTabIndex}
                  />
              </div>;
          },
          getValueMappingField              = ( field : Field | undefined, entry : ColumnFilter | undefined, fieldTabIndex : number ) => {
              const retVal = ( filterValue : any, filterMatchMode : FilterMatchMode, filterParams : FilterConfig, filterDisabled : boolean, filterOnchange : ( value : any, matchMode? : FilterMatchMode ) => void, filterLanguage : string ) => {
                  const hasMappingGroups = ( mappings?.[ 0 ] as unknown as FieldGroup )?.fields !== undefined;

                  let mapping : Field | undefined,
                      availableMappings : Fields = [],
                      fieldMarkDirty             = false;

                  if ( filterValue ) {
                      const fieldField = filterValue.replace( /^\$\{(.*)\}$/, '$1' );

                      if ( hasMappingGroups ) {
                          const mappingGroups : FieldGroup[] = [].concat( mappings as [] );

                          outerFor: for ( const mappingGroup of mappingGroups ) {
                              for ( const mappingItem of mappingGroup.fields ) {
                                  if ( mappingItem.field === fieldField ) {
                                      mapping = mappingItem;
                                      break outerFor;
                                  }
                              }
                          }
                      }
                      else {
                          const tempMappings : Field[] = [].concat( mappings as [] );

                          for ( const mappingItem of tempMappings ) {
                              if ( mappingItem.field === fieldField ) {
                                  mapping = mappingItem;
                                  break;
                              }
                          }
                      }
                  }

                  if ( hasMappingGroups ) {
                      const tempAvailableMappings : FieldGroup[] = [].concat( mappings as [] );

                      if ( limitMappingsByValueType ) {
                          for ( let k = tempAvailableMappings.length - 1; k >= 0; k-- ) {
                              tempAvailableMappings[ k ] = Object.assign( {}, tempAvailableMappings[ k ] );
                              tempAvailableMappings[ k ].fields = [].concat( tempAvailableMappings[ k ].fields as [] );

                              for ( let l = tempAvailableMappings[ k ].fields.length - 1; l >= 0; l-- ) {
                                  if ( !valueTypeToFieldTypeMapping[ field?.valueType ?? 'STRING' ]?.includes( tempAvailableMappings[ k ].fields[ l ].valueType as FormFieldProperties['type'] ) ) {
                                      tempAvailableMappings[ k ].fields.splice( l, 1 );

                                      if ( !tempAvailableMappings[ k ].fields.length ) {
                                          tempAvailableMappings.splice( k, 1 );
                                      }
                                  }
                              }
                          }
                      }

                      availableMappings = tempAvailableMappings;
                  }
                  else {
                      const tempAvailableMappings : Field[] = [].concat( mappings as [] );

                      if ( limitMappingsByValueType ) {
                          for ( let k = tempAvailableMappings.length - 1; k >= 0; k-- ) {
                              if ( !valueTypeToFieldTypeMapping[ field?.valueType ?? 'STRING' ]?.includes( tempAvailableMappings[ k ].valueType as FormFieldProperties['type'] ) ) {
                                  tempAvailableMappings.splice( k, 1 );
                              }
                          }
                      }

                      availableMappings = tempAvailableMappings;
                  }

                  if ( markDirty && entry ) {
                      const originalValueEntry = originalValue.find( oV => oV.field === entry.field );

                      if ( !originalValueEntry || originalValueEntry.value !== entry.value ) {
                          fieldMarkDirty = true;
                      }
                  }
                  else if ( markDirty && !elementValue?.length && JSON.stringify( originalValue ?? '' ) !== JSON.stringify( elementValue ?? '' ) ) {
                      fieldMarkDirty = true;
                  }

                  return <Dropdown
                      value={mapping}
                      options={availableMappings}
                      onChange={( event : DropdownChangeEvent ) => {
                          filterOnchange( `\$\{${event.value.field}\}` );
                      }}
                      optionLabel="label"
                      optionGroupLabel={hasMappingGroups ? 'label' : undefined}
                      optionGroupChildren={hasMappingGroups ? 'fields' : undefined}
                      style={{ width: '100%' }}
                      className={fieldMarkDirty ? 'yn-dirty-border' : undefined}
                      disabled={filterDisabled}
                      tabIndex={fieldTabIndex}
                  />;
              };
              retVal.displayName = 'mappingField';

              return retVal;
          },
          getOperatorDropdown               = ( field : Field | undefined, entry : ColumnFilter | undefined, fieldTabIndex : number ) => {
              let fieldMarkDirty   = false,
                  fieldMarkDefault = false;

              if ( entry ) {
                  if ( markDirty ) {
                      const originalEntry = originalValue.find( oV => oV.field === entry.field );

                      if (
                          !originalEntry
                          || originalEntry.operator !== entry.operator
                          || ( originalValue !== defaultFilters && elementValue === defaultFilters )
                      ) {
                          fieldMarkDirty = true;
                      }
                  }

                  if ( elementValue === defaultFilters ) {
                      fieldMarkDefault = true;
                  }
              }
              else if ( markDirty && !elementValue?.length && JSON.stringify( originalValue ?? '' ) !== JSON.stringify( elementValue ?? '' ) ) {
                  fieldMarkDirty = true;
              }

              return <div className={`${wrapperClassName} yn-jsonfield-filtersfield-operator${fieldMarkDirty ? ' yn-dirty-border' : ''}${fieldMarkDefault ? ' status-default' : ''}`}>
                  <FilterField
                      fieldKey={`${field?.field ?? 'new'}_op`}
                      field={`${field?.field ?? 'new'}_op`}

                      value={entry?.value}
                      valueType={entry?.valueType ?? field?.valueType ?? 'STRING'}
                      matchMode={entry?.operator}
                      showMatchModeSelector={true}
                      showMatchField={false}

                      disabled={!field ? true : disabled}
                      markDirty={false}
                      //tabIndex={fieldTabIndex}

                      params={{
                          schema,
                          timezone              : defaultBaseRequestParameters.timezone,
                          rawMode               : false,
                          showAdditionalRawNames: false,
                          valueSetId            : !entry?.value?.match( /^\$\{.*\}$/ ) ? field?.valueSetId : undefined,
                          valueUnit             : field?.valueUnit
                      }}

                      onChange={onOperatorChange.bind( null, field, entry )}
                  />
              </div>;
          },
          getValueField                     = ( field : Field | undefined, entry : ColumnFilter | undefined, fieldTabIndex : number ) => {
              let fieldMarkDirty   = false,
                  fieldMarkDefault = false;

              if ( entry ) {
                  if ( markDirty ) {
                      const originalValueEntry = originalValue.find( oV => oV.field === entry.field );

                      if (
                          !originalValueEntry
                          || originalValueEntry.value !== entry.value
                          || ( originalValue !== defaultFilters && elementValue === defaultFilters )
                      ) {
                          fieldMarkDirty = true;
                      }
                  }

                  if ( elementValue === defaultFilters ) {
                      fieldMarkDefault = true;
                  }
              }
              else if ( markDirty && !elementValue?.length && JSON.stringify( originalValue ?? '' ) !== JSON.stringify( elementValue ?? '' ) ) {
                  fieldMarkDirty = true;
              }

              return <div className={`${wrapperClassName} yn-jsonfield-filtersfield-value ${entry && ( entry?.value === '' || entry?.value === undefined || entry?.value === null ) ? ' yn-error-border' : ''}${fieldMarkDirty ? ' yn-dirty-border' : ''}${fieldMarkDefault ? ' status-default' : ''}`}>
                  <FilterField
                      fieldKey={`${field?.field ?? 'new'}_val`}
                      field={`${field?.field ?? 'new'}_val`}

                      value={entry?.value}
                      valueType={entry?.valueType ?? field?.valueType ?? 'STRING'}
                      matchMode={entry?.operator}
                      showMatchModeSelector={false}
                      showMatchField={true}

                      disabled={!field ? true : disabled}
                      markDirty={false}
                      //tabIndex={fieldTabIndex}

                      params={{
                          schema,
                          timezone              : defaultBaseRequestParameters.timezone,
                          rawMode               : false,
                          showAdditionalRawNames: false,
                          valueSetId            : field?.valueSetId,
                          valueUnit             : field?.valueUnit
                      }}

                      alternativeValueField={{
                          label   : __( 'YN.base.jsonField.filtersField.mapping.label' ),
                          icon    : 'fas fa-link',
                          active  : Boolean( entry?.value?.match( /(?:\$\{)((?:[\w\p{L}\p{N}:]+\.)+[\w\p{L}\p{N}:]+)(?:\})/gimu ) ),
                          getField: getValueMappingField( field, entry, fieldTabIndex )
                      }}

                      onChange={onValueChange.bind( null, field, entry )}
                  />
              </div>;
          };

    useEffect(
        () => {
            setElementValue( value ? value : defaultFilters );
        },
        [
            value,
            defaultFilters
        ]
    );

    return useMemo(
        () => {
            const retVal : React.ReactNode[] = [
                      <div
                          key={`${uid}_filter_labels`}
                          className="yn-jsonfield-entry yn-jsonfield-entry-label"
                      >
                          <span className="yn-jsonfield-delete-button-placeholder"/>

                          <div className={`${wrapperClassName} yn-jsonfield-filtersfield-field`}>
                              <label
                                  htmlFor={`${uid}_field`}
                                  className="block yn-sublabel"
                              >
                                  {__( 'YN.base.jsonField.filtersField.field.label' )}
                              </label>
                          </div>
                          <div className={`${wrapperClassName} yn-jsonfield-filtersfield-operator`}>
                              <label
                                  htmlFor={`${uid}_operator`}
                                  className="block yn-sublabel"
                              >
                                  {__( 'YN.base.jsonField.filtersField.operator.label' )}
                              </label>
                          </div>
                          <div className={`${wrapperClassName} yn-jsonfield-filtersfield-value`}>
                              <label
                                  htmlFor={`${uid}_value`}
                                  className="block yn-sublabel"
                              >
                                  {__( 'YN.base.jsonField.filtersField.value.label' )}
                              </label>
                          </div>
                      </div>
                  ],
                  hasFieldGroups             = ( fields?.[ 0 ] as unknown as FieldGroup )?.fields !== undefined;

            // elementValue entries rows
            for ( let i = 0; i < ( elementValue?.length ?? 0 ); i++ ) {
                const entry = elementValue![ i ];

                let field;

                if ( hasFieldGroups ) {
                    const fieldGroups : FieldGroup[] = [].concat( fields as [] );

                    outerFor: for ( const fieldGroup of fieldGroups ) {
                        for ( const fieldItem of fieldGroup.fields ) {
                            if ( fieldItem.field === entry.field ) {
                                field = fieldItem;
                                break outerFor;
                            }
                        }
                    }
                }
                else {
                    const tempFields : Field[] = [].concat( fields as [] );

                    for ( const fieldItem of tempFields ) {
                        if ( fieldItem.field === entry.field ) {
                            field = fieldItem;
                            break;
                        }
                    }
                }

                if ( field ) {
                    retVal.push(
                        <div
                            key={`${uid}_filters_container_${i}`}
                            className="yn-jsonfield-entry"
                        >
                            <Button
                                disabled={disabled}
                                icon="pi pi-times"
                                className="p-button-danger p-button-text yn-jsonfield-delete-button"
                                onClick={!disabled ? onDeleteButtonClick.bind( null, field, entry ) : undefined}
                                tabIndex={( tabIndex ?? 0 ) + ( 4 * i )}
                            />

                            {getField( field, entry, ( tabIndex ?? 0 ) + ( 4 * i ) + 1 )}
                            {getOperatorDropdown( field, entry, ( tabIndex ?? 0 ) + ( 4 * i ) + 2 )}
                            {getValueField( field, entry, ( tabIndex ?? 0 ) + ( 4 * i ) + 3 )}
                        </div>
                    );
                }
            }

            // new entry
            retVal.push(
                <div
                    key={`${uid}_filters_add`}
                    className="yn-jsonfield-entry"
                >
                    <span className="yn-jsonfield-delete-button-placeholder"/>

                    {getField( undefined, undefined, ( tabIndex ?? 0 ) + ( 4 * ( elementValue?.length ?? 0 ) ) + 1 )}
                    {getOperatorDropdown( undefined, undefined, ( tabIndex ?? 0 ) + ( 4 * ( elementValue?.length ?? 0 ) ) + 2 )}
                    {getValueField( undefined, undefined, ( tabIndex ?? 0 ) + ( 4 * ( elementValue?.length ?? 0 ) ) + 3 )}
                </div>
            );

            return <>{retVal}</>;
        },
        [
            disabled,
            elementValue,
            fields,
            language,
            mappings,
            messageCatalogTimestamp,
            originalValue,
            tabIndex,
            uid
        ]
    );
};
FiltersField.defaultProps = defaultProps;
FiltersField.displayName = 'JsonField-FiltersField';

export default FiltersField;
